import {
  MouseEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { useTerminalConfig } from '../../context/config-context';
import { useAgentState } from '../../providers/contexts';
import {
  settingsShortcutKeys,
  useSettingsActions,
  type SettingsSection,
} from '../../providers/SettingsProvider';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  DownloadIcon,
  LogoutIcon,
  SettingsIcon,
} from '../icons';
import { toLocalPosition, zoomFactorOf } from './menu-position';
import {
  MenuItem,
  MenuKey,
  MenuPositioned,
  MenuSeparator,
  MenuShortcut,
} from './menu';
import avatar1 from '../../assets/avatars/avatar-1.avif';
import avatar2 from '../../assets/avatars/avatar-2.avif';
import avatar3 from '../../assets/avatars/avatar-3.avif';
import avatar4 from '../../assets/avatars/avatar-4.avif';
import avatar5 from '../../assets/avatars/avatar-5.avif';

// ─────────────────────────────────────────────────────────────────────────
// Account identity: prefer the host-supplied profile (`config.user` —
// display name / email / avatar, e.g. from Clerk in the cloud flow). The
// gateway-URL derivation below is the FALLBACK for hosts without a profile
// (manual pairing against a per-user gateway like
// `mingjun-4275289e.terminal.dime.gg`). Behind the shared gateway-proxy the
// URL is the same for everyone (`gateway.dimeterminal.ai`), so URL-derived
// names there are wrong — cloud hosts must pass `config.user`.
// ─────────────────────────────────────────────────────────────────────────

// FALLBACK: derive a display username from the gateway URL. Rule: take the
// first subdomain label, then everything before its first `-`, capitalized.
// e.g. `wss://mingjun-4275289e.terminal.dime.gg` ⇒ `Mingjun`.
function deriveUsername(url: string | null | undefined): string {
  if (url == null || url.length === 0) return '';
  const host = url.replace(/^wss?:\/\//, '').split(/[/:?#]/)[0] ?? '';
  const subdomain = host.split('.')[0] ?? '';
  const name = subdomain.split('-')[0] ?? '';
  return name.length > 0 ? name.charAt(0).toUpperCase() + name.slice(1) : '';
}

// FALLBACK: placeholder profile pictures for hosts without a real avatar
// (`config.user.avatarUrl` wins when present). `avatarFor` hashes its seed
// (djb2) into a stable bucket so the same account always lands on the same
// picture.
const AVATARS: readonly string[] = [
  avatar1,
  avatar2,
  avatar3,
  avatar4,
  avatar5,
];

function avatarFor(seed: string | null | undefined): string | null {
  if (seed == null || seed.length === 0) return null;
  let h = 5381;
  for (let i = 0; i < seed.length; i += 1) {
    h = (Math.imul(h, 33) ^ seed.charCodeAt(i)) >>> 0;
  }
  return AVATARS[h % AVATARS.length] ?? null;
}

interface SidebarAccountButtonProps {
  /** Gateway URL — identity is derived from it (see TEMP notes above). */
  readonly gatewayLabel: string;
  /** Host "log out / unpair" action; the menu hides the item when omitted. */
  readonly onLogout?: () => void;
  /** Label for the logout action; default "Log Out" (see config.logoutLabel). */
  readonly logoutLabel?: string;
}

// Pinned footer account button at the bottom of the sidebar, plus its popup
// menu (gateway label + connection actions + "Log Out"). Owns its own
// open/close state. The avatar carries a status dot driven by the live agent
// connection so the user can see at a glance whether the gateway is up.
export default function SidebarAccountButton({
  gatewayLabel,
  onLogout,
  logoutLabel,
}: SidebarAccountButtonProps) {
  const state = useAgentState();
  const { onDownloadApp, user } = useTerminalConfig();
  const { openSettings } = useSettingsActions();
  // Host profile wins; the gateway-URL derivation is the no-profile fallback
  // (see the header note). Display-name fallback chain: profile name →
  // email local part → URL-derived.
  const username = useMemo(() => {
    const fromProfile = user?.displayName?.trim() ?? '';
    if (fromProfile.length > 0) return fromProfile;
    const emailLocal = user?.email?.split('@')[0]?.trim() ?? '';
    if (emailLocal.length > 0) return emailLocal;
    return deriveUsername(gatewayLabel);
  }, [user?.displayName, user?.email, gatewayLabel]);
  const avatarLetter = username.charAt(0).toUpperCase() || '?';
  const avatarSrc = useMemo(() => {
    if (user?.avatarUrl != null && user.avatarUrl.length > 0) {
      return user.avatarUrl;
    }
    // Stable placeholder: seed off the identity we display, not the shared
    // proxy URL (which would give every cloud user the same picture).
    return avatarFor(user?.email ?? gatewayLabel);
  }, [user?.avatarUrl, user?.email, gatewayLabel]);
  // Menu header + hover title: the user's email beats the gateway URL (behind
  // the shared proxy the URL identifies infrastructure, not the account).
  const accountLabel =
    user?.email != null && user.email.length > 0 ? user.email : gatewayLabel;
  const accountButtonRef = useRef<HTMLButtonElement>(null);
  const [accountMenuRect, setAccountMenuRect] = useState<DOMRect | null>(null);
  const toggleAccountMenu = useCallback(() => {
    setAccountMenuRect((cur) =>
      cur != null
        ? null
        : (accountButtonRef.current?.getBoundingClientRect() ?? null),
    );
  }, []);

  return (
    <>
      <AccountBar>
        <AccountButton
          ref={accountButtonRef}
          data-account-button
          type="button"
          onClick={toggleAccountMenu}
          title={accountLabel}
          aria-haspopup="menu"
          aria-expanded={accountMenuRect != null}
        >
          <AvatarSlot>
            {avatarSrc != null ? (
              <AccountAvatarImg src={avatarSrc} alt="" />
            ) : (
              <AccountAvatar>{avatarLetter}</AccountAvatar>
            )}
            <StatusDot $status={state.status} />
          </AvatarSlot>
          <AccountName>
            {username.length > 0 ? username : 'Account'}
          </AccountName>
          <ChevronIcon $open={accountMenuRect != null} />
        </AccountButton>
      </AccountBar>
      {accountMenuRect != null && (
        <AccountMenu
          anchorRect={accountMenuRect}
          accountLabel={accountLabel}
          hasLogout={onLogout != null}
          logoutLabel={logoutLabel}
          hasDownload={onDownloadApp != null}
          username={username}
          avatarSrc={avatarSrc}
          avatarLetter={avatarLetter}
          hasAccount={user != null}
          onClose={() => setAccountMenuRect(null)}
          onOpenSettings={(section) => {
            openSettings({
              section,
              // The account BUTTON, not the menu item: the item unmounts with
              // the menu, and focus cannot be restored to a detached node.
              returnFocusTo: accountButtonRef.current,
            });
            setAccountMenuRect(null);
          }}
          onDownload={() => {
            onDownloadApp?.();
            setAccountMenuRect(null);
          }}
          onLogout={() => {
            onLogout?.();
            setAccountMenuRect(null);
          }}
        />
      )}
    </>
  );
}

/**
 * Ceiling for the account menu's width. Past this the rows stop reading as a
 * list and start reading as a label at one edge and a value at the other.
 */
const MENU_MAX_WIDTH = 280;

// Popup for the sidebar account button. Anchors directly above the button
// (it lives at the very bottom of the sidebar), clamps into the viewport, and
// closes on click-away / Escape. Mirrors the FolderContextMenu portal pattern.
function AccountMenu({
  anchorRect,
  accountLabel,
  hasLogout,
  logoutLabel,
  hasDownload,
  onClose,
  onDownload,
  onLogout,
  onOpenSettings,
  username,
  avatarSrc,
  avatarLetter,
  hasAccount,
}: {
  readonly anchorRect: DOMRect;
  /** Header line: user email when the host supplies one, else gateway URL. */
  readonly accountLabel: string;
  /** Display name, resolved by the bar (profile → email local part → URL). */
  readonly username: string;
  readonly avatarSrc: string | null;
  readonly avatarLetter: string;
  readonly hasLogout: boolean;
  readonly logoutLabel?: string;
  readonly hasDownload: boolean;
  readonly onClose: () => void;
  readonly onDownload: () => void;
  readonly onLogout: () => void;
  readonly onOpenSettings: (section?: SettingsSection) => void;
  /** True when an Account section exists to deep-link into. */
  readonly hasAccount: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ x: number; y: number }>(() => ({
    x: anchorRect.left,
    y: anchorRect.top,
  }));
  const [minWidth, setMinWidth] = useState(180);

  useEffect(() => {
    function onClickAway(e: globalThis.MouseEvent) {
      const target = e.target as HTMLElement;
      if (target.closest('[data-account-menu]') != null) return;
      // The button toggles the menu itself; ignore clicks on it here so the
      // toggle isn't immediately undone by this click-away handler.
      if (target.closest('[data-account-button]') != null) return;
      onClose();
    }
    function onEsc(e: globalThis.KeyboardEvent) {
      if (e.key !== 'Escape') return;
      // Claim the key. Escape dismisses ONE layer, and this menu is the top
      // one while it is open — a modal listening on document capture must not
      // collapse on the same press. Window capture is also the public
      // contract the settings dialog uses to dismiss this menu when it opens
      // over it (a synthetic Escape on window — see its mount effect).
      e.stopPropagation();
      onClose();
    }
    window.addEventListener('mousedown', onClickAway, true);
    window.addEventListener('keydown', onEsc, true);
    return () => {
      window.removeEventListener('mousedown', onClickAway, true);
      window.removeEventListener('keydown', onEsc, true);
    };
  }, [onClose]);

  // Place the menu just above the anchor, left-aligned, clamped on-screen.
  // Measured after layout so the menu height is known before paint.
  useLayoutEffect(() => {
    const el = ref.current;
    if (el == null) return;
    const rect = el.getBoundingClientRect();
    const margin = 8;
    const gap = 6;
    const maxX = window.innerWidth - rect.width - margin;
    // Clamped in screen space (rect and innerWidth agree there), then converted
    // into the element's own space — see menu-position: under UI scale a screen
    // coordinate written straight to "left"/"top" gets scaled a second time.
    setPos(
      toLocalPosition(
        el,
        Math.max(margin, Math.min(anchorRect.left, maxX)),
        Math.max(margin, anchorRect.top - rect.height - gap),
      ),
    );
    // Wide enough to read, not as wide as the button.
    //
    // This used to be `max(180, anchorWidth)`, which tied the menu to the
    // account button — and that button spans the whole sidebar, so the menu
    // stretched with it and the rows ended up marooned at opposite edges. The
    // comparable menus in other apps are all a fixed, narrower panel: the
    // trigger's width says nothing about how much room a list of five short
    // labels needs. Clamped instead, with the anchor only setting the floor,
    // so a narrow sidebar still gets a menu wide enough to read.
    //
    // The anchor's width is a screen measurement, so it needs the same
    // conversion as the position before it can be used as a CSS length.
    const anchorWidth = anchorRect.width / zoomFactorOf(el);
    setMinWidth(Math.min(MENU_MAX_WIDTH, Math.max(220, anchorWidth)));
  }, [anchorRect]);

  return createPortal(
    <MenuPositioned
      ref={ref}
      data-account-menu
      role="menu"
      style={{ left: pos.x, top: pos.y, minWidth, maxWidth: MENU_MAX_WIDTH }}
    >
      {/* Identity first: which account these actions apply to, before the
          actions themselves. */}
      {accountLabel.length > 0 && (
        <>
          {/* The identity block is itself the route to Account: clicking your
              own name to manage your account is the behaviour people already
              expect, and it saves a redundant "Account" row in a menu that
              already shows exactly who you are. Interactive only when there IS
              an account section to land on — with no host-supplied user the
              section is gated off and this would open on something else. */}
          {hasAccount ? (
            <AccountMenuIdentityButton
              type="button"
              role="menuitem"
              aria-label={`Account settings for ${username}`}
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                onOpenSettings('account');
              }}
            >
              <IdentityContent
                username={username}
                accountLabel={accountLabel}
                avatarSrc={avatarSrc}
                avatarLetter={avatarLetter}
              />
              <IdentityChevron />
            </AccountMenuIdentityButton>
          ) : (
            <AccountMenuIdentity>
              <IdentityContent
                username={username}
                accountLabel={accountLabel}
                avatarSrc={avatarSrc}
                avatarLetter={avatarLetter}
              />
            </AccountMenuIdentity>
          )}
          <MenuSeparator />
        </>
      )}
      <MenuItem
        type="button"
        role="menuitem"
        onClick={(e: MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          onOpenSettings();
        }}
      >
        <SettingsIcon />
        Settings
        <MenuShortcut aria-hidden="true">
          {settingsShortcutKeys().map((key) => (
            <MenuKey key={key}>{key}</MenuKey>
          ))}
        </MenuShortcut>
      </MenuItem>
      {hasDownload && (
        <MenuItem
          type="button"
          role="menuitem"
          onClick={(e: MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            onDownload();
          }}
        >
          <DownloadIcon />
          Download App
        </MenuItem>
      )}
      {hasLogout && (
        <>
          {/* Its own group, but NOT `$danger`. The separator already says "this
              one is different in kind"; red says "this will destroy something",
              which logging out does not — you sign back in. That colour is
              reserved for the irreversible actions (deleting a chat or a
              folder, see SessionRow / FolderRow), and spending it here would
              make it mean less there. */}
          <MenuSeparator />
          <MenuItem
            type="button"
            role="menuitem"
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              onLogout();
            }}
          >
            <LogoutIcon />
            {logoutLabel ?? 'Log Out'}
          </MenuItem>
        </>
      )}
    </MenuPositioned>,
    document.body,
  );
}

// Pinned footer holding the account button. Sticks to the bottom of the
// scrolling sidebar (Container is the scroll parent); `margin-top: auto`
// drops it to the bottom when the chats list is short.
const AccountBar = styled.div`
  position: sticky;
  bottom: 0;
  margin-top: auto;
  flex-shrink: 0;
  padding: 8px;
  background: var(--bg-color-page);
  border-top: 1px solid var(--divider-color);
`;

// Matches the rounded-corner radius modern Chromium PWA windows draw at
// their outer chrome — picking the same value here makes the account bar
// read as part of the window's own frame rather than a contrasting pill.
const AccountButton = styled.button`
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 12px;
  border-radius: 10px;
  border: none;
  background: transparent;
  color: var(--text-primary);
  font-family: inherit;
  font-size: 12px;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: var(--hover-bg);
  }
`;

// Right-aligned chevron that flips when the popup opens. Sits at the end
// of the row so it reads as a disclosure indicator rather than competing
// with the account name.
const ChevronIcon = styled(ChevronDownIcon)<{ readonly $open: boolean }>`
  margin-left: auto;
  color: var(--text-secondary);
  transition: transform 0.15s ease;
  transform: ${(p) => (p.$open ? 'rotate(180deg)' : 'rotate(0)')};
`;

// Wraps the avatar so the connection-status dot can absolute-position
// itself in the bottom-right corner without affecting layout.
const AvatarSlot = styled.span`
  position: relative;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
`;

// Connection-status indicator overlaid on the avatar. Same color rules as
// the old top-bar dot — accent when authenticated/connected, amber while
// the handshake is in flight, error on a failed connect, muted otherwise.
const StatusDot = styled.span<{ $status: string }>`
  position: absolute;
  right: -1px;
  bottom: -1px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  border: 2px solid var(--bg-color-page);
  background: ${({ $status }) => {
    switch ($status) {
      case 'connected':
      case 'authenticated':
        return 'var(--accent-color)';
      case 'connecting':
      case 'authenticating':
        return 'var(--pending-color)';
      case 'error':
        return 'var(--error-color)';
      default:
        return 'var(--text-secondary)';
    }
  }};
`;

// Circular monogram avatar — first letter of the username, Google-style.
const AccountAvatar = styled.span`
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--accent-color);
  color: var(--text-primary);
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
`;

// Image variant of the avatar — same circular footprint as the monogram.
const AccountAvatarImg = styled.img`
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  border-radius: 50%;
  object-fit: cover;
  display: block;
`;

const AccountName = styled.span`
  font-size: 13px;
  font-weight: 400;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Muted gateway-URL line at the top of the account menu (where the old
// top-bar gateway label used to live).
/**
 * Identity block at the top of the menu: avatar, display name, and the address
 * the actions below apply to. Two lines rather than one because the name and
 * the address answer different questions ("who am I signed in as" vs "which
 * account is that"), and on the cloud path they differ.
 */
/**
 * Avatar + name + address. Shared by the interactive and static variants of the
 * identity block so the two cannot drift apart visually.
 */
function IdentityContent({
  username,
  accountLabel,
  avatarSrc,
  avatarLetter,
}: {
  readonly username: string;
  readonly accountLabel: string;
  readonly avatarSrc: string | null;
  readonly avatarLetter: string;
}) {
  return (
    <>
      <AccountMenuAvatar>
        {avatarSrc != null ? (
          <AccountAvatarImg src={avatarSrc} alt="" />
        ) : (
          <AccountAvatar aria-hidden="true">{avatarLetter}</AccountAvatar>
        )}
      </AccountMenuAvatar>
      <AccountMenuIdentityText>
        <AccountMenuName title={username}>{username}</AccountMenuName>
        <AccountMenuAddress title={accountLabel}>
          {accountLabel}
        </AccountMenuAddress>
      </AccountMenuIdentityText>
    </>
  );
}

const AccountMenuIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  min-width: 0;
`;

const AccountMenuIdentityButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px;
  min-width: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  font-family: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: var(--hover-bg);
  }
`;

/** Chevron hinting that the identity block leads somewhere. */
const IdentityChevron = styled(ChevronRightIcon)`
  margin-left: auto;
  color: var(--text-label);
`;

const AccountMenuAvatar = styled.span`
  position: relative;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AccountMenuIdentityText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const AccountMenuName = styled.span`
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AccountMenuAddress = styled.span`
  font-size: 0.6875rem;
  color: var(--text-label);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
